import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse } from '@/api/types'
import type { StockReportApiResponse, StockReportRow } from '@/modules/inventory/reports/types'

export interface StockReportQueryParams {
  warehouseId: number
  itemId: number
  reportDate: string
  pageNumber: number
  pageSize: number
}

function normalizeRow(raw: Record<string, unknown>): StockReportRow {
  const g = (k: string, alt?: string) => {
    const v = raw[k] ?? (alt ? raw[alt] : undefined)
    return v
  }
  return {
    itemCode: String(g('itemCode', 'ItemCode') ?? ''),
    name: String(g('name', 'Name') ?? ''),
    category: String(g('category', 'Category') ?? ''),
    subCategory: String(g('subCategory', 'SubCategory') ?? ''),
    uom: String(g('uom', 'Uom') ?? ''),
    type: (g('type', 'Type') as string | null | undefined) ?? null,
    warehouse: String(g('warehouse', 'Warehouse') ?? ''),
    batchCode: String(g('batchCode', 'BatchCode') ?? ''),
    availableQuantity: Number(g('availableQuantity', 'AvailableQuantity') ?? 0),
    damagedStock: Number(g('damagedStock', 'DamagedStock') ?? 0),
    expiredStock: Number(g('expiredStock', 'ExpiredStock') ?? 0),
    holdStock: Number(g('holdStock', 'HoldStock') ?? 0),
    totalQuantity: Number(g('totalQuantity', 'TotalQuantity') ?? 0),
    unitSalesPrice: Number(g('unitSalesPrice', 'UnitSalesPrice') ?? 0),
    unitCostPrice: Number(g('unitCostPrice', 'UnitCostPrice') ?? 0),
    expiryDate: String(g('expiryDate', 'ExpiryDate') ?? ''),
  }
}

function extractPayload(responseData: unknown, params: StockReportQueryParams): StockReportApiResponse {
  const fallback = (): StockReportApiResponse => ({
    totalRecords: 0,
    pageNumber: params.pageNumber,
    pageSize: params.pageSize,
    data: [],
  })

  if (!responseData || typeof responseData !== 'object') {
    return fallback()
  }

  const root = responseData as Record<string, unknown>

  const inner =
    'data' in root &&
    root.data &&
    typeof root.data === 'object' &&
    !Array.isArray(root.data) &&
    'data' in (root.data as object)
      ? (root.data as Record<string, unknown>)
      : root

  const rowsRaw = inner.data
  const data: StockReportRow[] = Array.isArray(rowsRaw)
    ? rowsRaw.map((r) => normalizeRow(r as Record<string, unknown>))
    : []

  const totalRecords = Number(inner.totalRecords ?? inner.TotalRecords ?? data.length) || 0
  const pageNumber = Number(inner.pageNumber ?? inner.PageNumber ?? params.pageNumber) || params.pageNumber
  const pageSize = Number(inner.pageSize ?? inner.PageSize ?? params.pageSize) || params.pageSize

  return { totalRecords, pageNumber, pageSize, data }
}

export class StockReportService {
  async getStockReport(params: StockReportQueryParams): Promise<StockReportApiResponse> {
    const url = endpoints.reports.stockReport(params)
    const response = await apiClient.get<
      ApiResponse<StockReportApiResponse> | StockReportApiResponse | StockReportRow[]
    >(url)

    const d = response.data

    if (Array.isArray(d)) {
      return {
        data: d.map((r) => normalizeRow(r as unknown as Record<string, unknown>)),
        totalRecords: d.length,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      }
    }

    if (d && typeof d === 'object' && 'data' in d && 'totalRecords' in d) {
      return extractPayload(d, params)
    }

    if (d && typeof d === 'object' && 'success' in d && 'data' in d) {
      const wrapped = d as ApiResponse<StockReportApiResponse>
      return extractPayload(wrapped.data, params)
    }

    return extractPayload(d, params)
  }
}

export const stockReportService = new StockReportService()
